/**
 * Receipt for a charge.
 *
 * Drawn from scratch rather than filled, like the deal payment receipt: there
 * is no state form for "we took five hundred dollars for a deposit" and no
 * template to lay text into.
 *
 * It is deliberately not the deal receipt with fields blanked out. That one
 * talks about a balance, because a deal has one; a charge does not, and a
 * receipt that says "Balance remaining: $0.00" invites the question of what
 * the balance was. This says what was collected, what it was for, and
 * nothing it cannot stand behind.
 *
 * Both sides build it from the same code — the desk, with a Supabase session,
 * and the customer's own browser on a payment link, where the figures come
 * back from the worker instead. That is the reason the input here is a plain
 * shape and not FormContext, which cannot be assembled without a deal.
 */

import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const INK = rgb(0.12, 0.16, 0.20);
const SOFT = rgb(0.40, 0.45, 0.49);
const RULE = rgb(0.85, 0.88, 0.91);
const NAVY = rgb(0.10, 0.16, 0.32);
const RED = rgb(0.70, 0.21, 0.17);

const usd = (c: number) =>
  (c / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

const longDate = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-US",
        { year: "numeric", month: "long", day: "numeric" });
};

const timeOf = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "" : d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

export interface ChargeReceiptDealer {
  legalName: string;
  dba: string | null;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  flhsmvLicenseNo: string;
}

export interface ChargeReceiptData {
  chargeNo: string;
  amountCents: number;
  description: string | null;
  customerName: string;
  /** Optional, and only known on the desk's side. */
  customerAddress?: string | null;
  vehicle: string | null;
  vin: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  paidAt: string | null;
  /** Set once the money has gone back, so a refunded charge cannot be
      reprinted as though it were still money in hand. */
  refundedAt?: string | null;
  dealer: ChargeReceiptDealer | null;
}

/** US 10-digit phone, left alone if it is not one. */
const prettyPhone = (raw: string | null | undefined) => {
  const d = String(raw ?? "").replace(/\D/g, "");
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (raw ?? "");
};

const cardLine = (brand: string | null, last4: string | null) => {
  if (!brand && !last4) return "Card";
  const b = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Card";
  return last4 ? `${b} ending ${last4}` : b;
};

export async function buildChargeReceipt(d: ChargeReceiptData): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage([612, 792]);
  const reg = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);
  const mono = await doc.embedFont(StandardFonts.Courier);

  const refunded = Boolean(d.refundedAt);
  let y = 742;

  const text = (t: string, x: number, size = 10, f = reg, color = INK) =>
    page.drawText(t, { x, y, size, font: f, color });
  const right = (t: string, xr: number, size = 10, f = reg, color = INK) =>
    page.drawText(t, { x: xr - f.widthOfTextAtSize(t, size), y, size, font: f, color });
  const rule = (yy: number, from = 48, to = 564, c = RULE) =>
    page.drawLine({ start: { x: from, y: yy }, end: { x: to, y: yy },
                    thickness: 0.75, color: c });

  /* Wrap on words. The description is free text typed at a counter and will
     sooner or later be a paragraph. */
  const wrap = (t: string, width: number, size: number, f = reg) => {
    const out: string[] = [];
    let line = "";
    for (const word of t.split(/\s+/)) {
      const next = line ? `${line} ${word}` : word;
      if (f.widthOfTextAtSize(next, size) > width && line) { out.push(line); line = word; }
      else line = next;
    }
    if (line) out.push(line);
    return out;
  };

  // ----- Header
  text(refunded ? "REFUNDED" : "RECEIPT", 48, 20, bold, refunded ? RED : NAVY);
  right(d.chargeNo, 564, 12, bold, SOFT);
  y -= 17;

  const dealer = d.dealer;
  text(dealer ? (dealer.dba || dealer.legalName) : "", 48, 11, bold);
  right(longDate(d.paidAt), 564, 10, reg, SOFT);
  y -= 13;
  if (dealer) {
    text(`${dealer.street}, ${dealer.city}, ${dealer.state} ${dealer.zip}`, 48, 9, reg, SOFT);
    const t = timeOf(d.paidAt);
    if (t) right(t, 564, 9, reg, SOFT);
    y -= 11;
    const tail = [prettyPhone(dealer.phone), dealer.email].filter(Boolean).join("  ·  ");
    if (tail) { text(tail, 48, 9, reg, SOFT); y -= 11; }
    if (dealer.flhsmvLicenseNo) {
      text(`Dealer license ${dealer.flhsmvLicenseNo}`, 48, 9, reg, SOFT);
      y -= 11;
    }
  }
  /* The box below starts 30pt under y and stands 56pt tall, so the rule needs
     more clearance than the 24pt it had: at 24 the box covered it in every
     branch and the separator the layout is built around never showed. */
  y -= 8; rule(y); y -= 34;

  // ----- The amount, said once and large
  page.drawRectangle({
    x: 48, y: y - 30, width: 516, height: 56,
    color: refunded ? rgb(0.98, 0.93, 0.92) : rgb(0.94, 0.96, 0.99),
  });
  page.drawText(refunded ? "Amount refunded" : "Amount paid",
    { x: 64, y: y + 8, size: 9, font: reg, color: SOFT });
  page.drawText(usd(d.amountCents),
    { x: 64, y: y - 18, size: 24, font: bold, color: refunded ? RED : NAVY });
  {
    const label = cardLine(d.cardBrand, d.cardLast4);
    page.drawText(label, {
      x: 548 - reg.widthOfTextAtSize(label, 10), y: y - 10,
      size: 10, font: reg, color: INK,
    });
    page.drawText("Paid by", {
      x: 548 - reg.widthOfTextAtSize("Paid by", 9), y: y + 8,
      size: 9, font: reg, color: SOFT,
    });
  }
  y -= 54;

  // ----- Who and what for
  const pair = (label: string, value: string, f = reg) => {
    text(label, 48, 9, reg, SOFT);
    text(value, 170, 10, f);
    y -= 16;
  };
  pair("Received from", d.customerName || "—");
  if (d.customerAddress) pair("Address", d.customerAddress);
  if (d.vehicle) pair("Vehicle", d.vehicle);
  if (d.vin) {
    text("VIN", 48, 9, reg, SOFT);
    text(d.vin, 170, 10, mono);
    y -= 16;
  }

  if (d.description) {
    y -= 6;
    text("For", 48, 9, reg, SOFT);
    const lines = wrap(d.description, 394, 10);
    for (const [i, line] of lines.entries()) {
      page.drawText(line, { x: 170, y: y - i * 13, size: 10, font: reg, color: INK });
    }
    y -= Math.max(16, lines.length * 13 + 3);
  }

  y -= 8; rule(y); y -= 22;

  if (refunded) {
    page.drawRectangle({
      x: 48, y: y - 26, width: 516, height: 34, color: rgb(0.98, 0.93, 0.92),
    });
    page.drawText(
      `This payment was refunded in full on ${longDate(d.refundedAt ?? null)}.`,
      { x: 62, y: y - 14, size: 11, font: bold, color: RED });
    y -= 46;
  } else {
    page.drawRectangle({
      x: 48, y: y - 26, width: 516, height: 34, color: rgb(0.91, 0.95, 0.92),
    });
    page.drawText("Paid in full. Nothing further is owed on this receipt.",
      { x: 62, y: y - 14, size: 11, font: bold, color: rgb(0.17, 0.42, 0.28) });
    y -= 46;
  }

  // ----- Footer
  y = 72;
  for (const [i, line] of wrap(
    "This receipt records money collected for the item described above. It is "
    + "not a bill of sale, does not transfer title, and does not by itself "
    + "reserve or hold a vehicle. Keep the reference number shown at the top "
    + "for any question about this payment.", 516, 8).entries()) {
    page.drawText(line, { x: 48, y: y - i * 10, size: 8, font: reg, color: SOFT });
  }

  doc.setTitle(`${d.chargeNo} receipt`);
  doc.setProducer("FrontLot");
  return doc.save();
}
